// AI Chain of Thought Component — OFFSET
// « Réflexion » repliable : label mono uppercase, contenu italic muted.
// Le px-mark (state thinking) est le seul indicateur d'activité.

import React, { memo, useState } from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { PxMark } from '@/components/PxMark';

export interface AIChainOfThoughtProps {
    title?: string;
    defaultOpen?: boolean;
    isLoading?: boolean;
    className?: string;
    children: React.ReactNode;
}

export const AIChainOfThought = memo(({
    title = 'Réflexion',
    defaultOpen = false,
    isLoading = false,
    className,
    children
}: AIChainOfThoughtProps) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div
            className={cn(
                "border border-border bg-card overflow-hidden",
                className
            )}
        >
            {/* Header */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={cn(
                    "w-full flex items-center gap-2 px-3 py-2 text-left",
                    "hover:bg-secondary transition-colors"
                )}
            >
                <PxMark size={12} state={isLoading ? "thinking" : "idle"} />

                <span className="flex-1 font-mono text-[11px] uppercase tracking-[0.1em] text-faint">
                    {title}
                    {isLoading && <span className="ml-1.5 normal-case tracking-normal">…</span>}
                </span>

                <ChevronDown
                    className={cn(
                        "h-3 w-3 text-faint transition-transform duration-200",
                        isOpen && "rotate-180"
                    )}
                />
            </button>

            {/* Content */}
            <AnimatePresence initial={false}>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.18, ease: 'easeOut' }}
                    >
                        <div className="px-3 pb-3 pt-2 text-xs italic leading-relaxed text-muted-foreground border-t border-border">
                            {children}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
});

AIChainOfThought.displayName = 'AIChainOfThought';

// Chain of Thought Step (for multi-step reasoning)
export interface AIChainOfThoughtStepProps {
    icon?: React.ReactNode;
    status?: 'pending' | 'running' | 'complete';
    className?: string;
    children: React.ReactNode;
}

export const AIChainOfThoughtStep = memo(({
    icon,
    status = 'complete',
    className,
    children
}: AIChainOfThoughtStepProps) => {
    return (
        <div className={cn("flex items-start gap-2 py-1", className)}>
            <div className="h-4 w-4 flex items-center justify-center shrink-0 mt-0.5">
                {icon || (
                    <>
                        {status === 'running' && <PxMark size={10} state="thinking" />}
                        {status === 'complete' && <Check className="h-3 w-3 text-ok" />}
                        {status === 'pending' && <span className="h-1.5 w-1.5 bg-faint/40" />}
                    </>
                )}
            </div>
            <span className={cn(
                "text-xs",
                status === 'running' && "text-foreground",
                status === 'complete' && "text-muted-foreground",
                status === 'pending' && "text-faint"
            )}>
                {children}
            </span>
        </div>
    );
});

AIChainOfThoughtStep.displayName = 'AIChainOfThoughtStep';

export default AIChainOfThought;
